/**
 * API Configuration
 * 
 * Centralized configuration for API base URL
 * Uses environment variable FRONTEND_URL if set, otherwise defaults to backend URL
 */

import axios from 'axios';
import { requestDeduplicator } from '../utils/requestDeduplicator';
import { clearProductSearchCache } from '../utils/productSearchCache';

/**
 * The current browser origin (e.g. `https://yellowgreen-manatee-122289.hostingersite.com`),
 * or '' during SSR/build where `window` is unavailable. Used as the fallback so a
 * missing/misconfigured REACT_APP_FRONTEND_URL falls back to same-origin requests
 * instead of a hardcoded domain (which would cause cross-origin CORS failures).
 */
const RUNTIME_ORIGIN =
  typeof window !== 'undefined' && window.location ? window.location.origin : '';

/**
 * Origin only — no trailing slash, no trailing `/api`.
 * If REACT_APP_* is set to `https://example.com/api`, axios would otherwise combine with
 * paths like `/api/products` → `https://example.com/api/api/products` (wrong route / 5xx).
 */
function normalizeApiOrigin(url) {
  if (!url || typeof url !== 'string') return RUNTIME_ORIGIN;
  return url.replace(/\/+$/, '').replace(/\/api$/i, '');
}

// Single application origin. Create React App only exposes REACT_APP_* vars to
// the bundle, so the frontend reads REACT_APP_FRONTEND_URL (set from the root
// .env by scripts/build-frontend.js). API, backend, and frontend all share it.
// Fall back to the live browser origin (same-origin) rather than a hardcoded
// domain so a missing env var can never trigger a cross-origin CORS block.
const APP_ORIGIN = process.env.REACT_APP_FRONTEND_URL || RUNTIME_ORIGIN;

const BACKEND_URL = normalizeApiOrigin(APP_ORIGIN);
const FRONTEND_URL = APP_ORIGIN.replace(/\/+$/, '');
const API_BASE_URL = normalizeApiOrigin(APP_ORIGIN);

// Timeout constants (ms)
export const API_TIMEOUT_DEFAULT = 10000;
export const API_TIMEOUT_UPLOAD = 60000;
export const API_TIMEOUT_LONG = 15000;

// API path builders – use these instead of hardcoded strings
export const apiPaths = {
  auth: {
    me: () => '/api/auth/me',
    login: () => '/api/auth/login',
    logout: () => '/api/auth/logout',
    refresh: () => '/api/auth/refresh',
    register: () => '/api/auth/register',
    forgotPassword: () => '/api/auth/forgot-password',
    verifyOtp: () => '/api/auth/verify-otp',
    resetPassword: () => '/api/auth/reset-password',
  },
  admin: {
    stats: () => '/api/admin/dashboard/stats',
    login: () => '/api/admin/login',
    profile: (id) => `/api/admin/${id}`,
  },
  products: (page = 1, limit = 20, search = '', category = '') => {
    const p = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) p.append('search', search);
    if (category) p.append('category', category);
    return `/api/products?${p.toString()}`;
  },
  productsSuggest: (q, limit = 5) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (q) p.set('q', q);
    return `/api/products/suggest?${p.toString()}`;
  },
  product: (id) => `/api/products/${id}`,
  categories: (page = 1, limit = 100) => {
    const p = new URLSearchParams({ page: String(page), limit: String(limit) });
    return `/api/categories?${p.toString()}`;
  },
  category: (id) => `/api/categories/${id}`,
  cart: (userId) => `/api/cart/${userId}`,
  cartAdd: () => '/api/cart/add',
  cartUpdate: () => '/api/cart/update',
  cartRemove: () => '/api/cart/remove',
  customers: () => '/api/customers',
  customer: (id) => `/api/customers/${id}`,
  mechanicalSealAttributes: (id) => `/api/mechanical-seal-attributes/${id}`,
  mechanicalSealAttributesBatch: () => '/api/mechanical-seal-attributes/batch',
  csrfToken: () => '/api/csrf-token',
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_DEFAULT,
  withCredentials: true, // ✅ send httpOnly auth cookie
  headers: {
    'Content-Type': 'application/json'
  }
});

// Note: Request deduplication is handled via the deduplicatedGet method
// or by using the requestDeduplicator directly in components

// ----------------------------
// CSRF handling (memory-first)
// ----------------------------
let csrfToken = null;
let csrfTokenPromise = null;
let csrfTokenExpiresAt = 0;
const CSRF_STORAGE_KEY = 'csrf_token_fallback';
const CSRF_EXPIRY_STORAGE_KEY = 'csrf_token_expires_at';
const CSRF_REFRESH_BUFFER_MS = 30 * 1000;

const AUTH_BOOTSTRAP_PATHS = [
  '/api/auth/login',
  '/api/admin/login',
  '/api/auth/register',
  '/api/auth/logout',
  '/api/auth/refresh',
  '/api/auth/forgot-password',
  '/api/auth/verify-otp',
  '/api/auth/reset-password',
];

let refreshTokenPromise = null;
let onSessionExpired = null;
let authRefreshDisabled = false;

export const registerSessionExpiredHandler = (handler) => {
  onSessionExpired = handler;
};

export const resetAuthRefreshState = () => {
  authRefreshDisabled = false;
};

const hasStoredSessionHint = () => {
  try {
    return Boolean(
      sessionStorage.getItem('auth_cache') ||
      sessionStorage.getItem('admin_auth_ts')
    );
  } catch {
    return false;
  }
};

const canAttemptTokenRefresh = () => !authRefreshDisabled && hasStoredSessionHint();

const isAuthBootstrapPath = (url = '') =>
  AUTH_BOOTSTRAP_PATHS.some((path) => String(url).includes(path));

const triggerSessionExpired = async () => {
  if (typeof onSessionExpired === 'function') {
    await onSessionExpired();
  }
};

const refreshAccessToken = async () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = api
      .post(apiPaths.auth.refresh(), {}, { _skipAuthRefresh: true })
      .finally(() => {
        refreshTokenPromise = null;
      });
  }
  return refreshTokenPromise;
};

export const clearAllAuthStorage = () => {
  authRefreshDisabled = true;
  clearCsrfTokenState();
  const keys = [
    'auth_cache',
    'admin_auth_ts',
    'admin_logout_intent',
    'admin_logout_ts',
  ];
  keys.forEach((key) => removeSessionStorage(key));
};

const readSessionStorage = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionStorage = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage write failures
  }
};

const removeSessionStorage = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore storage remove failures
  }
};

const clearCsrfTokenState = () => {
  csrfToken = null;
  csrfTokenExpiresAt = 0;
  removeSessionStorage(CSRF_STORAGE_KEY);
  removeSessionStorage(CSRF_EXPIRY_STORAGE_KEY);
};

const setCsrfTokenState = (token, expiresInSeconds) => {
  if (!token) {
    clearCsrfTokenState();
    return;
  }

  const ttlSeconds = Number(expiresInSeconds);
  const fallbackTtlSeconds = 150 * 60; // fallback to backend default TTL
  const resolvedTtlSeconds = Number.isFinite(ttlSeconds) && ttlSeconds > 0
    ? ttlSeconds
    : fallbackTtlSeconds;

  csrfToken = token;
  csrfTokenExpiresAt = Date.now() + (resolvedTtlSeconds * 1000);

  writeSessionStorage(CSRF_STORAGE_KEY, token);
  writeSessionStorage(CSRF_EXPIRY_STORAGE_KEY, String(csrfTokenExpiresAt));
};

const hasUsableCsrfToken = () => {
  if (!csrfToken || !csrfTokenExpiresAt) return false;
  return Date.now() < (csrfTokenExpiresAt - CSRF_REFRESH_BUFFER_MS);
};

const restoreCsrfTokenFromStorage = () => {
  const storedToken = readSessionStorage(CSRF_STORAGE_KEY);
  const storedExpiryRaw = readSessionStorage(CSRF_EXPIRY_STORAGE_KEY);
  const storedExpiry = Number(storedExpiryRaw);

  if (!storedToken || !Number.isFinite(storedExpiry)) {
    clearCsrfTokenState();
    return false;
  }

  if (Date.now() >= (storedExpiry - CSRF_REFRESH_BUFFER_MS)) {
    clearCsrfTokenState();
    return false;
  }

  csrfToken = storedToken;
  csrfTokenExpiresAt = storedExpiry;
  return true;
};

const shouldAttachCsrf = (config) => {
  const method = (config.method || 'get').toUpperCase();
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return false;

  const url = String(config.url || '');
  // Do not attach/fetch CSRF for token issuance itself
  if (url.includes('/api/csrf-token')) return false;

  // Bootstrapping routes: CSRF not required initially
  const noCsrfPaths = [
    '/api/auth/login',
    '/api/admin/login',
    '/api/auth/register',
    '/api/auth/refresh',
    '/api/auth/forgot-password',
    '/api/auth/verify-otp',
    '/api/auth/reset-password',
  ];
  if (noCsrfPaths.some((p) => url.includes(p))) return false;

  return true;
};

const fetchCsrfToken = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh) {
    // 1) memory with expiry check
    if (hasUsableCsrfToken()) return csrfToken;

    // 2) sessionStorage fallback (refresh survival) with expiry check
    if (restoreCsrfTokenFromStorage()) {
      return csrfToken;
    }
  }

  // 3) dedup in-flight fetch
  if (!csrfTokenPromise) {
    csrfTokenPromise = api
      .get('/api/csrf-token', { deduplicate: true })
      .then((res) => {
        const token = res?.data?.csrfToken;
        const expiresIn = res?.data?.expiresIn;
        if (token) {
          setCsrfTokenState(token, expiresIn);
        }
        return csrfToken;
      })
      .finally(() => {
        csrfTokenPromise = null;
      });
  }

  return csrfTokenPromise;
};

export const ensureFreshCsrfToken = async () => {
  return fetchCsrfToken();
};

// Request interceptor: attach CSRF token for mutations
api.interceptors.request.use(async (config) => {
  if (shouldAttachCsrf(config)) {
    const token = await ensureFreshCsrfToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = token;
    }
  }
  return config;
});

// Response interceptor to cache results and clear cache on mutations
api.interceptors.response.use(
  (response) => {
    // Clear relevant cache entries on mutations
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(response.config.method?.toUpperCase())) {
      const url = response.config.url || '';
      if (url.includes('/products')) {
        requestDeduplicator.clearResultCache('/products');
        clearProductSearchCache();
      }
      if (url.includes('/categories')) {
        requestDeduplicator.clearResultCache('/categories');
      }
      if (url.includes('/cart')) {
        requestDeduplicator.clearResultCache('/cart');
      }
    }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const config = error?.config;
    const method = (config?.method || '').toUpperCase();
    const isMutation = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    const url = String(config?.url || '');

    const responseCode = error?.response?.data?.code;
    const isSessionAuthError =
      status === 401 ||
      (status === 403 &&
        (responseCode === 'AUTH_SESSION_EXPIRED' || responseCode === 'AUTH_TOKEN_INVALID'));
    const canAttemptRefresh =
      config &&
      !config._authRetry &&
      !config._skipAuthRefresh &&
      isSessionAuthError &&
      !isAuthBootstrapPath(url) &&
      canAttemptTokenRefresh();

    if (status === 403 && isMutation) {
      const responseBody = error?.response?.data;
      const errorText = String(responseBody?.error ?? '').toLowerCase();
      const messageText = String(responseBody?.message ?? '').toLowerCase();
      const isCsrfError = errorText.includes('csrf') || messageText.includes('csrf');

      // Auto-retry once if this was specifically a CSRF rejection
      if (isCsrfError && config && !config._csrfRetry) {
        config._csrfRetry = true;
        try {
          clearCsrfTokenState();
          const newToken = await fetchCsrfToken({ forceRefresh: true });
          if (newToken) {
            config.headers = config.headers || {};
            config.headers['X-CSRF-Token'] = newToken;
          }
          return api(config);
        } catch (retryErr) {
          return Promise.reject(retryErr);
        }
      }
    }

    if (canAttemptRefresh) {
      config._authRetry = true;
      try {
        await refreshAccessToken();
        return api(config);
      } catch (refreshErr) {
        authRefreshDisabled = true;
        await triggerSessionExpired();
        return Promise.reject(refreshErr);
      }
    }

    if (status === 401) {
      if (url.includes('/api/auth/me')) {
        try {
          sessionStorage.removeItem('admin_auth_ts');
        } catch {
          /* ignore */
        }
      }
    }

    return Promise.reject(error);
  }
);

// Enhanced deduplicated GET method - wraps axios.get with deduplication
api.deduplicatedGet = (url, config = {}, cacheTTL = 2000) => {
  return requestDeduplicator.dedupe(
    { method: 'GET', url, ...config },
    () => api.get(url, config),
    cacheTTL
  );
};

// Override get method to use deduplication by default for GET requests
const originalGet = api.get.bind(api);
api.get = function(url, config = {}) {
  // Only deduplicate GET requests
  if (config.deduplicate !== false) {
    return requestDeduplicator.dedupe(
      { method: 'GET', url, ...config },
      () => originalGet(url, config),
      2000 // 2 second cache TTL
    );
  }
  return originalGet(url, config);
};

const normalizeCategoryName = (value = '') =>
  String(value).trim().toLowerCase();

// Shared category identifiers
const MECHANICAL_SEAL_CATEGORY_NAME =
  (process.env.MECHANICAL_SEAL_CATEGORY_NAME || 'Mechanical/ Water Pump Seals').trim();

const MECHANICAL_SEAL_CATEGORY_SLUG = normalizeCategoryName(MECHANICAL_SEAL_CATEGORY_NAME);

/**
 * Builds a full API URL using the configured base URL.
 * Ensures the path begins with a leading slash.
 *
 * @param {string} path - API path, with or without leading slash.
 * @returns {string} - Full URL such as "https://eme6.com/api/categories".
 */
export const buildApiUrl = (path = '') => {
  if (!path) {
    return API_BASE_URL;
  }

  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

export const getMechanicalSealCategoryName = () => MECHANICAL_SEAL_CATEGORY_NAME;
export const getMechanicalSealCategorySlug = () => MECHANICAL_SEAL_CATEGORY_SLUG;
export const normalizeCategory = normalizeCategoryName;

/**
 * Clears the cached CSRF token (memory + sessionStorage).
 * Call this immediately after any login so the next mutation request
 * fetches a fresh token bound to the authenticated user's session.
 */
export const clearCsrfToken = () => {
  clearCsrfTokenState();
};

export {
  API_BASE_URL as default,
  API_BASE_URL,
  BACKEND_URL,
  FRONTEND_URL,
  MECHANICAL_SEAL_CATEGORY_NAME,
  MECHANICAL_SEAL_CATEGORY_SLUG,
  normalizeCategoryName,
  api,
};
